/* eslint-disable max-len */
import {API_ENDPOINT_JSON} from '../../../../../config';
import {
  conditionType,
  inputType
} from '../../../../../pages/esgManager/interface/inputType';
import {FieldConditions} from '../../../interface/form-conditions';

import {
  DETAILS_OF_PROCEDURE_OPTIONS,
  FREQUENCY_OF_ASSESSMENT_OPTIONS,
  INDUSTRY_SECTORS_OPTIONS,
  PARTNERS_AND_STAKEHOLDERS_CONSIDERED_OPTIONS,
  PRIMARY_REASONS_FOR_NOT_ASSESSING_INTERCONNECTIONS_OPTIONS,
  PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
  PROCESS_PLACE_OPTIONS,
  PROJECT_OPTIONS,
  RISK_TYPES_AND_CRITERIA_OPTIONS,
  RIVER_BASIN_OPTIONS,
  SUPPLIER_TIER_OPTIONS,
  TOOLS_AND_METHODS_OPTIONS,
  TYPE_OF_ASSESSMENT_OPTIONS
} from './corporateOptions';
import {
  CDP_CORPORATE_INTRO_FIELD_NAMES,
  CDP_CORPORATE_MANAGEMENT_FIELD_NAMES
} from './corporateQuestionsKeys';

export const corporateManagementQuestions = [
  {
    key: '1',
    showIndex: false,
    items: [
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.1 How does your organization define short-, medium-, and long-term time horizons in relation to the identification, assessment, and management of your environmental dependencies, impacts, risks, and opportunities?',
            tooltip:
              'Understanding organizations’ definitions of time horizons allows data users to contextualize the different timescales at which organizations consider environmental issues in the identification, assessment, and management of dependencies, impacts, risks, and opportunities, and how this informs their business strategy and financial planning. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q1_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q1_1_VALUE,
                  fieldLabel: 'Time horizon',
                  options: [
                    {value: 'Short-term', label: 'Short-term'},
                    {value: 'Medium-term', label: 'Medium-term'},
                    {value: 'Long-term', label: 'Long-term'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q1_2_VALUE,
                  fieldLabel: 'From (years)',
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q1_3_VALUE,
                  fieldLabel: 'Is your long-term time horizon open ended?',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q1_4_VALUE,
                  fieldLabel: 'To (years)',
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q1_5_VALUE,
                  fieldLabel:
                    'How this time horizon is linked to strategic and/or financial planning',
                  colSpan: true,
                  textLimit: 1500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2 Does your organization have a process for identifying, assessing, and managing environmental dependencies and/or impacts? ',
            tooltip:
              'Dependencies and impacts on the environment can result in changes to the capacity of nature to provide social and economic functions. Additionally, it is essential to identify, assess, and manage dependencies and impacts on the environment in order to assess effectively the risks and opportunities of an organization. This question allows data users to gauge the organization’s awareness of its own environmental dependencies and impacts.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_1_VALUE,
                  fieldLabel: 'Process in place',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_2_VALUE,
                  fieldLabel:
                    'Dependencies and/or impacts evaluated in this process',
                  options: [
                    {value: 'Dependencies only', label: 'Dependencies only'},
                    {value: 'Impacts only', label: 'Impacts only'},
                    {
                      value: 'Both dependencies and impacts',
                      label: 'Both dependencies and impacts'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_3_VALUE,
                  fieldLabel:
                    'Biodiversity impacts evaluated before the mining project development stage',
                  options: [
                    {value: 'Yes, in all cases', label: 'Yes, in all cases'},
                    {value: 'Yes, in some cases', label: 'Yes, in some cases'},
                    {value: 'No', label: 'No'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_4_VALUE,
                  fieldLabel:
                    'Primary reason for not evaluating dependencies and/or impacts',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_5_VALUE,
                  fieldLabel:
                    'Explain why you do not evaluate dependencies and/or impacts and describe any plans to do so in the future',
                  colSpan: true,
                  textLimit: 2500,
                  conditions: {
                    dependsOn: [
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_1_VALUE,
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_2_VALUE
                    ],
                    shouldDisable: {
                      expression: {
                        or: [
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_1_VALUE,
                            operator: 'equals',
                            value: 'Yes'
                          },
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_2_VALUE,
                            operator: 'equals',
                            value: 'Both dependencies and impacts'
                          }
                        ]
                      }
                    },
                    shouldRequired: {
                      expression: {
                        or: [
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_1_VALUE,
                            operator: 'notEquals',
                            value: 'Yes'
                          },
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_2_VALUE,
                            operator: 'notEquals',
                            value: 'Both dependencies and impacts'
                          }
                        ]
                      }
                    }
                  } satisfies FieldConditions
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2.1 Does your organization have a process for identifying, assessing, and managing environmental risks and/or opportunities?',
            tooltip:
              'For many organizations, environmental issues pose significant challenges, now and in the future. This question establishes whether the organization has a process for identifying, assessing, and managing environmental issues so that data users may gauge the organizations awareness of its own environmental risks and opportunities.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_1_VALUE,
                  fieldLabel: 'Process in place',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_2_VALUE,
                  fieldLabel:
                    'Risks and/or opportunities evaluated in this process',
                  options: [
                    {value: 'Risks only', label: 'Risks only'},
                    {value: 'Opportunities only', label: 'Opportunities only'},
                    {
                      value: 'Both risks and opportunities',
                      label: 'Both risks and opportunities'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_3_VALUE,
                  fieldLabel:
                    'Is this process informed by the dependencies and/or impacts process?',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.SELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_4_VALUE,
                  fieldLabel:
                    'Primary reason for not evaluating risks and/or opportunities',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_5_VALUE,
                  fieldLabel:
                    'Explain why you do not evaluate risks and/or opportunities and describe any plans to do so in the future',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_1_VALUE
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_6_VALUE,
                  fieldLabel:
                    'Explain why you do not have a process for evaluating both risks and opportunities that is informed by a dependencies and/or impacts process',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_2_VALUE,
                    condition: conditionType.NOT,
                    values: ['Risks only', 'Opportunities only']
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Risks only', 'Opportunities only'],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_2_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_VALUE,
            fieldLabel:
              '2.2.2 Provide details of your organization’s process for identifying, assessing, and managing environmental dependencies, impacts, risks, and/or opportunities.',
            tooltip:
              'Organizations that have established a comprehensive, recurring procedure to identify, assess, and manage environmental dependencies, impacts, risks, and opportunities across their valuechain and over a range of time-horizons will be better equipped to handle longer-term uncertainties and liabilities, as well as capitalize on opportunities. This question indicates to data users how robust an organization’s assessment process is.',
            heading: inputType.HEADING,
            colSpan: true,
            conditions: {
              dependsOn: [
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_1_VALUE,
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_1_VALUE
              ],
              shouldShow: {
                expression: {
                  or: [
                    {
                      parentField:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_VALUE,
                      field:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_1_VALUE,
                      operator: 'equals',
                      value: 'Yes'
                    },
                    {
                      parentField:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_VALUE,
                      field:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q3_1_VALUE,
                      operator: 'equals',
                      value: 'Yes'
                    }
                  ]
                }
              }
            } satisfies FieldConditions,
            formList: {
              formFields: [
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_1_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Plastics', label: 'Plastics'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_2_VALUE,
                  fieldLabel:
                    'Indicate which of dependencies, impacts, risks, and opportunities are covered by the process for this environmental issue',
                  options: [
                    {value: 'Dependencies', label: 'Dependencies'},
                    {value: 'Impacts', label: 'Impacts'},
                    {value: 'Risks', label: 'Risks'},
                    {value: 'Opportunities', label: 'Opportunities'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_3_VALUE,
                  fieldLabel: 'Value chain stages covered',
                  options: [
                    {value: 'Direct operations', label: 'Direct operations'},
                    {
                      value: 'Upstream value chain',
                      label: 'Upstream value chain'
                    },
                    {
                      value: 'Downstream value chain [not shown to FS]',
                      label: 'Downstream value chain [not shown to FS]'
                    },
                    {
                      value: 'End of life management',
                      label: 'End of life management'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_4_VALUE,
                  fieldLabel: 'Coverage',
                  options: [
                    {value: 'Full', label: 'Full'},
                    {value: 'Partial', label: 'Partial'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Direct operations',
                      'Upstream value chain',
                      'Downstream value chain [not shown to FS]'
                    ]
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_5_VALUE,
                  fieldLabel: 'Supplier tiers covered',
                  options: SUPPLIER_TIER_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Upstream value chain']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_6_VALUE,
                  fieldLabel: 'Mining projects covered',
                  options: PROJECT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_7_VALUE,
                  fieldLabel: 'Type of assessment',
                  options: TYPE_OF_ASSESSMENT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_8_VALUE,
                  fieldLabel: 'Frequency of assessment',
                  options: FREQUENCY_OF_ASSESSMENT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_9_VALUE,
                  fieldLabel: 'Time horizons covered',
                  options: [
                    {value: 'Short-term', label: 'Short-term'},
                    {value: 'Medium-term', label: 'Medium-term'},
                    {value: 'Long-term', label: 'Long-term'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_10_VALUE,
                  fieldLabel: 'Integration of risk management process',
                  options: [
                    {
                      value:
                        'Integrated into multi-disciplinary organizationwide risk management process',
                      label:
                        'Integrated into multi-disciplinary organizationwide risk management process'
                    },
                    {
                      value: 'A specific environmental risk management process',
                      label: 'A specific environmental risk management process'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_2_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Risks']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_11_VALUE,
                  fieldLabel: 'Location specificity used',
                  options: [
                    {value: 'Site-specific', label: 'Site-specific'},
                    {value: 'Local', label: 'Local'},
                    {value: 'Sub-national', label: 'Sub-national'},
                    {value: 'National', label: 'National'},
                    {
                      value: 'Not location specific',
                      label: 'Not location specific'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Direct operations',
                      'Upstream value chain',
                      'Downstream value chain [not shown to FS]'
                    ]
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_12_VALUE,
                  fieldLabel: 'Tools and methods used',
                  options: TOOLS_AND_METHODS_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_13_VALUE,
                  fieldLabel: 'Risk types and criteria considered',
                  options: RISK_TYPES_AND_CRITERIA_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_2_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Risks']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_14_VALUE,
                  fieldLabel: 'Partners and stakeholders considered',
                  options: PARTNERS_AND_STAKEHOLDERS_CONSIDERED_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_15_VALUE,
                  fieldLabel:
                    'Has this process changed since the previous reporting year?',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_16_VALUE,
                  fieldLabel: 'Further details of process',
                  colSpan: true,
                  textLimit: 3500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_4_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Full']
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_4_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Partial'],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q4_4_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            controlHeading: {
              fieldType: inputType.HEADING,
              heading:
                '2.2.3 Provide mining-specific details of your organization’s process for identifying, assessing, and managing biodiversity impacts.',
              tooltip:
                'Assessing biodiversity impacts before the development of a mining project as part of an Environmental Impact Assessment (EIA) improves decision making and resource allocation, as well as helping to avoid costs associated with unforeseen issues. Indicating how your projects have gone through such assessment before the project development stage provides evidence to data users that biodiversity impacts have been considered before implementation of the project.'
            },
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_VALUE,
            dependsOn:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_3_VALUE,
            showWhen: ['Yes, in all cases', 'Yes, in some cases'],
            relatedTableKey:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q2_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_1_VALUE,
                  fieldLabel: 'Mining project ID',
                  options: PROJECT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_2_VALUE,
                  fieldLabel: 'Extent of assessment',
                  options: [
                    {
                      value:
                        'Full-scale environmental and social impact assessment',
                      label:
                        'Full-scale environmental and social impact assessment'
                    },
                    {
                      value:
                        'A limited or focused environmental and social assessment',
                      label:
                        'A limited or focused environmental and social assessment'
                    },
                    {
                      value:
                        'Straightforward application of environmental siting, pollution standards, design criteria, or construction standards',
                      label:
                        'Straightforward application of environmental siting, pollution standards, design criteria, or construction standards'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_3_VALUE,
                  fieldLabel: 'Impacts considered',
                  options: [
                    {value: 'Direct impacts', label: 'Direct impacts'},
                    {value: 'Indirect impacts', label: 'Indirect impacts'},
                    {value: 'Cumulative impacts', label: 'Cumulative impacts'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_4_VALUE,
                  fieldLabel: 'Scope defined by',
                  options: [
                    {
                      value: 'Government agency requirements',
                      label: 'Government agency requirements'
                    },
                    {
                      value: 'Lender requirements',
                      label: 'Lender requirements'
                    },
                    {
                      value: 'Company own standards and/or policies',
                      label: 'Company own standards and/or policies'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_5_VALUE,
                  fieldLabel: 'Aspects considered',
                  options: [
                    {
                      value: 'Alternative locations',
                      label: 'Alternative locations'
                    },
                    {value: 'Threatened species', label: 'Threatened species'},
                    {value: 'Migratory species', label: 'Migratory species'},
                    {value: 'Endemic species', label: 'Endemic species'},
                    {value: 'Protected habitats', label: 'Protected habitats'},
                    {value: 'Critical habitats', label: 'Critical habitats'},
                    {value: 'Natural habitats', label: 'Natural habitats'},
                    {value: 'Ecosystem services', label: 'Ecosystem services'},
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_6_VALUE,
                  fieldLabel: 'Baseline biodiversity data available',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_7_VALUE,
                  fieldLabel:
                    'Environmental Impact Statement publicly available ',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_8_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 3500,
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_6_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q5_6_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2.4 Does your organization have a process for identifying, assessing, and managing environmental dependencies and/or impacts related to your portfolio activities?',
            tooltip:
              'Dependencies and impacts on the environment can result in changes to the capacity of nature to provide social and economic functions. Additionally, it is essential for financial institutions to identify, assess, and manage dependencies and impacts on the environment in order to assess effectively the risks and opportunities related to their various portfolio activities. This question allows data users to gauge the organization’s awareness of its own environmental dependencies and impacts'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_VALUE,
            scopeColumnTitle: 'Portfolio',
            fixedRows: [
              {
                id: '1',
                label: 'Banking (Bank)'
              },
              {
                id: '2',
                label: 'Investing (Asset manager)'
              },
              {
                id: '3',
                label: 'Investing (Asset owner)'
              },
              {
                id: '4',
                label: 'Insurance underwriting (Insurance company)'
              }
            ],
            formList: {
              formFields: [
                // {
                //   type: inputType.SELECT,
                //   fieldName: CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_1_VALUE, // into 1.10
                //   fieldLabel: 'Portfolio',
                //   colSpan: true
                // },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_2_VALUE,
                  fieldLabel: 'Process in place covering this portfolio',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_3_VALUE,
                  fieldLabel:
                    'Dependencies and/or impacts related to this portfolio evaluated in this process',
                  options: [
                    {value: 'Dependencies only', label: 'Dependencies only'},
                    {value: 'Impacts only', label: 'Impacts only'},
                    {
                      value: 'Both dependencies and impacts',
                      label: 'Both dependencies and impacts'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_4_VALUE,
                  fieldLabel:
                    'Primary reason for not evaluating dependencies and/or impacts related to this portfolio',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_5_VALUE,
                  fieldLabel:
                    'Explain why you do not evaluate dependencies and/or impacts related to this portfolio and describe any plans to evaluate this in the future',
                  colSpan: true,
                  textLimit: 2500,
                  conditions: {
                    dependsOn: [
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_2_VALUE,
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_3_VALUE
                    ],
                    shouldDisable: {
                      expression: {
                        or: [
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_2_VALUE,
                            operator: 'equals',
                            value: 'Yes'
                          },
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_3_VALUE,
                            operator: 'equals',
                            value: 'Both dependencies and impacts'
                          }
                        ]
                      }
                    },
                    shouldRequired: {
                      expression: {
                        or: [
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_2_VALUE,
                            operator: 'notEquals',
                            value: 'Yes'
                          },
                          {
                            field:
                              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_3_VALUE,
                            operator: 'notEquals',
                            value: 'Both dependencies and impacts'
                          }
                        ]
                      }
                    }
                  } satisfies FieldConditions
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2.5 Does your organization have a process for identifying, assessing, and managing environmental risks and/or opportunities related to your portfolio activities?',
            tooltip:
              'For many organizations, environmental issues pose significant challenges, now and in the future. This question establishes whether the financial institution has a process for identifying, assessing, and managing environmental issues related to their various portfolio activities so that data users may gauge the organization’s awareness of its own environmental risks and opportunities.'
          },
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_VALUE,
            scopeColumnTitle: 'Portfolio',
            fixedRows: [
              {
                id: '1',
                label: 'Banking (Bank)'
              },
              {
                id: '2',
                label: 'Investing (Asset manager)'
              },
              {
                id: '3',
                label: 'Investing (Asset owner)'
              },
              {
                id: '4',
                label: 'Insurance underwriting (Insurance company)'
              }
            ],
            formList: {
              formFields: [
                // {
                //   type: inputType.SELECT,
                //   fieldName: CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_1_VALUE, // into 1.10
                //   fieldLabel: 'Portfolio',
                //   colSpan: true
                // },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_2_VALUE,
                  fieldLabel: 'Process in place covering this portfolio',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_3_VALUE,
                  fieldLabel:
                    'Risks and/or opportunities related to this portfolio are evaluated in this process',
                  options: [
                    {value: 'Risks only', label: 'Risks only'},
                    {value: 'Opportunities only', label: 'Opportunities only'},
                    {
                      value: 'Both risks and opportunities',
                      label: 'Both risks and opportunities'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_4_VALUE,
                  fieldLabel:
                    'Is this process informed by the dependencies and/or impacts process?',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.SELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_5_VALUE,
                  fieldLabel:
                    'Primary reason for not evaluating risks and/or opportunities related to this portfolio',
                  options:
                    PRIMARY_REASONS_FOR_NOT_IMPLEMENTING_ENVIRONMENTAL_INITIATIVES_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_6_VALUE,
                  fieldLabel:
                    'Explain why you do not evaluate risks and/or opportunities related to this portfolio and describe any plans to do so in the future',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_2_VALUE
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_7_VALUE,
                  fieldLabel:
                    'Explain why you do not have a process for evaluating both risks and opportunities related to this portfolio that is informed by a dependencies and/or impacts process',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_3_VALUE,
                    condition: conditionType.NOT,
                    values: ['Risks only', 'Opportunities only']
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_3_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Risks only', 'Opportunities only'],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_3_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_VALUE,
            controlHeading: {
              fieldType: inputType.HEADING,
              heading:
                '2.2.6 Provide details of your organization’s process for identifying, assessing, and managing environmental dependencies, impacts, risks, and/or opportunities related to your portfolio activities',
              tooltip:
                'Financial institutions that have established a comprehensive, recurring procedure to identify, assess, and manage environmental dependencies, impacts, risks, and opportunities across all their portfolios and over a range of time-horizons will be better equipped to handle longer-term uncertainties and liabilities, as well as capitalize on opportunities. This question indicates to data users how robust a financial institution’s assessment process is. '
            },
            conditions: {
              dependsOn: [
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_2_VALUE,
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_3_VALUE
              ],
              shouldShow: {
                expression: {
                  or: [
                    {
                      field:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q6_2_VALUE,
                      operator: 'equals',
                      value: 'Yes'
                    },
                    {
                      parentField:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_VALUE,
                      field:
                        CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q7_2_VALUE,
                      operator: 'equals',
                      value: 'Yes'
                    }
                  ]
                }
              }
            } satisfies FieldConditions,
            scopeColumnTitle: 'Portfolio',
            fixedRows: [
              {
                id: '1',
                label: 'Banking (Bank)'
              },
              {
                id: '2',
                label: 'Investing (Asset manager)'
              },
              {
                id: '3',
                label: 'Investing (Asset owner)'
              },
              {
                id: '4',
                label: 'Insurance underwriting (Insurance company)'
              }
            ],
            formList: {
              formFields: [
                // {
                //   type: inputType.SELECT,
                //   fieldName: CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_1_VALUE,
                //   fieldLabel: 'Portfolio',
                //   options: [
                //     {value: 'Banking (Bank)', label: 'Banking (Bank)'},
                //     {value: 'Investing (Asset manager)', label: 'Investing (Asset manager)'},
                //     {value: 'Investing (Asset owner)', label: 'Investing (Asset owner)'},
                //     {value: 'Insurance underwriting (Insurance company)', label: 'Insurance underwriting (Insurance company)'}
                //   ],
                //   colSpan: true
                // },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_2_VALUE,
                  fieldLabel: 'Environmental issue',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'},
                    {value: 'Plastics', label: 'Plastics'},
                    {value: 'Biodiversity', label: 'Biodiversity'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_3_VALUE,
                  fieldLabel:
                    'Indicate which of dependencies, impacts, risks,and opportunities  are covered by  the process for  this portfolio',
                  options: [
                    {value: 'Dependencies', label: 'Dependencies'},
                    {value: 'Impacts', label: 'Impacts'},
                    {value: 'Risks', label: 'Risks'},
                    {value: 'Opportunities', label: 'Opportunities'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_4_VALUE,
                  fieldLabel:
                    '% of portfolio covered by the assessment process in  relation to  total  portfolio  value ',
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_5_VALUE,
                  fieldLabel: 'Type of assessment',
                  options: TYPE_OF_ASSESSMENT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_6_VALUE,
                  fieldLabel: 'Type of assessment',
                  options: INDUSTRY_SECTORS_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_7_VALUE,
                  fieldLabel: 'Frequency of assessment',
                  options: FREQUENCY_OF_ASSESSMENT_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_8_VALUE,
                  fieldLabel: 'Time horizons covered',
                  options: [
                    {value: 'Short-term', label: 'Short-term'},
                    {value: 'Medium-term', label: 'Medium-term'},
                    {value: 'Long-term', label: 'Long-term'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_9_VALUE,
                  fieldLabel: 'Integration of risk management process',
                  options: [
                    {
                      value:
                        'Integrated into multi-disciplinary organizationwide risk management process',
                      label:
                        'Integrated into multi-disciplinary organizationwide risk management process'
                    },
                    {
                      value: 'A specific environmental risk management process',
                      label: 'A specific environmental risk management process'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Risks']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_10_VALUE,
                  fieldLabel: 'Location specificity used',
                  options: [
                    {value: 'Site-specific', label: 'Site-specific'},
                    {value: 'Local', label: 'Local'},
                    {value: 'Sub-national', label: 'Sub-national'},
                    {value: 'National', label: 'National'},
                    {
                      value: 'Not location specific',
                      label: 'Not location specific'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_11_VALUE,
                  fieldLabel: 'Tools and methods used',
                  options: TOOLS_AND_METHODS_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_12_VALUE,
                  fieldLabel: 'Risk types and criteria considered',
                  options: RISK_TYPES_AND_CRITERIA_OPTIONS,
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_3_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Risks']
                  }
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_13_VALUE,
                  fieldLabel: 'Partners and stakeholders considered',
                  options: PARTNERS_AND_STAKEHOLDERS_CONSIDERED_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_14_VALUE,
                  fieldLabel: 'Further details of process',
                  colSpan: true,
                  textLimit: 2500,
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q8_4_VALUE,
                    condition: conditionType.LESS_THAN,
                    numericValue: 100,
                    required: true
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2.7 Are the interconnections between environmental dependencies, impacts, risks and/or opportunities assessed?',
            tooltip:
              'Nature-related disclosures should be integrated with other business and sustainability-related disclosures, whenever possible, to provide report users with an integrated and holistic picture of the organization’s financial position and prospects. Integration of climate- and nature-related disclosures is of particular importance. It is important that any alignment, synergies, contributions and possible trade-offs between climate and nature are clearly identified. This question demonstrates that assessing the interconnections between environmental dependencies, impacts, risks and opportunities is part of the organization’s assessment process.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.RADIOGROUP,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE,
                  fieldLabel:
                    'Interconnections between environmental dependencies, impacts, risks and/or opportunities assessed',
                  options: ['Yes', 'No'],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_2_VALUE,
                  fieldLabel:
                    'Description of how interconnections are assessed',
                  colSpan: true,
                  textLimit: 3000,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE,
                    condition: conditionType.NOT,
                    values: ['Yes']
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['Yes'],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_3_VALUE,
                  fieldLabel:
                    'Primary reason for not assessing interconnections between environmental dependencies, impacts, risks and/or opportunities',
                  options:
                    PRIMARY_REASONS_FOR_NOT_ASSESSING_INTERCONNECTIONS_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_4_VALUE,
                  fieldLabel:
                    'Explain why you do not assess the interconnections between environmental dependencies, impacts, risks and/or opportunities',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE,
                    condition: conditionType.NOT,
                    values: ['No']
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: ['No'],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q9_1_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2.8  Does your organization consider environmental information about your clients/investees as part of your due diligence and/or environmental dependencies, impacts, risks and/or opportunities assessment process?',
            tooltip:
              'Considering environmental information about clients/investees in the initial phases of a financial institution’s assessment of dependencies, impacts, risks, and opportunities and/or as part of their due diligence process helps data users and the organization understand their value chain’s exposure to environmental risks and opportunities. This question helps attest to the robustness of an organization’s assessment of environmental issues to data users.',
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_VALUE
          },
          {
            fieldType: inputType.ADD_FIXED_ROW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_VALUE,
            scopeColumnTitle: 'Portfolio',
            fixedRows: [
              {
                id: '1',
                label: 'Banking (Bank)'
              },
              {
                id: '2',
                label: 'Investing (Asset manager)'
              },
              {
                id: '3',
                label: 'Investing (Asset owner)'
              },
              {
                id: '4',
                label: 'Insurance underwriting (Insurance company)'
              }
            ],
            formList: {
              formFields: [
                // {
                //   type: inputType.SELECT,
                //   fieldName: CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_1_VALUE,
                //   fieldLabel: 'Portfolio',
                //   options: [
                //     {value: 'Banking (Bank)', label: 'Banking (Bank)'},
                //     {value: 'Investing (Asset manager)', label: 'Investing (Asset manager)'},
                //     {value: 'Investing (Asset owner)', label: 'Investing (Asset owner)'},
                //     {value: 'Insurance underwriting (Insurance company)', label: 'Insurance underwriting (Insurance company)'}
                //   ],
                //   colSpan: true
                // },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_2_VALUE,
                  fieldLabel: 'We consider environmental information',
                  options: PROCESS_PLACE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_3_VALUE,
                  fieldLabel:
                    'Explain why you do not consider environmental information',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_2_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_2_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_2_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.2.9 Indicate the environmental information your organization considers about clients/investees as part of your due diligence and/or environmental dependencies, impacts, risks and/or opportunities assessment process, and how this influences decision-making.',
            tooltip:
              'Considering environmental information about clients/investees in the initial phases of a financial institutions’ assessment of dependencies, impacts, risks, and opportunities and/or as part of their due diligence process helps data users and the organization understand their value chain’s exposure to environmental risks and opportunities. Data users are interested in what information financial institutions consider, about which clients/investees, and whether that is enough for them to make informed lending, investment and/or insurance underwriting decisions and thus mitigate environmental risks within their portfolio',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_VALUE,
              showWhen: ['Yes'],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_2_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_VALUE,
            dependsOn:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_2_VALUE,
            showWhen: ['Yes'],
            relatedTableKey:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q10_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_1_VALUE,
                  fieldLabel: 'Portfolio',
                  options: [
                    {value: 'Banking (Bank)', label: 'Banking (Bank)'},
                    {
                      value: 'Investing (Asset manager)',
                      label: 'Investing (Asset manager)'
                    },
                    {
                      value: 'Investing (Asset owner)',
                      label: 'Investing (Asset owner)'
                    },
                    {
                      value: 'Insurance underwriting (Insurance company)',
                      label: 'Insurance underwriting (Insurance company)'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_2_VALUE,
                  fieldLabel: 'Environmental issues covered',
                  options: [
                    {value: 'Climate change', label: 'Climate change'},
                    {value: 'Forests', label: 'Forests'},
                    {value: 'Water', label: 'Water'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_3_VALUE,
                  fieldLabel: 'Type of environmental information considered',
                  options: [
                    {
                      value: 'Emissions data [C only]',
                      label: 'Emissions data [C only]'
                    },
                    {
                      value: 'Energy usage data [C only]',
                      label: 'Energy usage data [C only]'
                    },
                    {
                      value: 'Emissions reduction targets [C only]',
                      label: 'Emissions reduction targets [C only]'
                    },
                    {
                      value: 'Climate transition plans [C only]',
                      label: 'Climate transition plans [C only]'
                    },
                    {
                      value: 'CDP questionnaire response',
                      label: 'CDP questionnaire response'
                    },
                    {value: 'CDP scores', label: 'CDP scores'},
                    {
                      value: 'TCFD disclosures [C only]',
                      label: 'TCFD disclosures [C only]'
                    },
                    {
                      value: 'Science-Based Net-Zero Targets [C only]',
                      label: 'Science-Based Net-Zero Targets [C only]'
                    },
                    {
                      value: 'TNFD disclosures [W, F]',
                      label: 'TNFD disclosures [W, F]'
                    },
                    {
                      value: 'Scope and content of forests policy [F only]',
                      label: 'Scope and content of forests policy [F only]'
                    },
                    {
                      value:
                        'Commitment to eliminate deforestation and conversion of other natural ecosystems [F only]',
                      label:
                        'Commitment to eliminate deforestation and conversion of other natural ecosystems [F only]'
                    },
                    {
                      value: 'Commodity volumes [F only]',
                      label: 'Commodity volumes [F only]'
                    },
                    {
                      value: 'Certification of commodities [F only]',
                      label: 'Certification of commodities [F only]'
                    },
                    {
                      value:
                        'Proportion of commodity volumes in compliance with no deforestation and conversion [F only]',
                      label:
                        'Proportion of commodity volumes in compliance with no deforestation and conversion [F only]'
                    },
                    {
                      value: 'Traceability of commodities [F only]',
                      label: 'Traceability of commodities [F only]'
                    },
                    {
                      value: 'Origin of commodities [F only]',
                      label: 'Origin of commodities [F only]'
                    },
                    {
                      value: 'Scope and content of water policy [W only]',
                      label: 'Scope and content of water policy [W only]'
                    },
                    {
                      value:
                        'Water withdrawal and/or consumption volumes [W only]',
                      label:
                        'Water withdrawal and/or consumption volumes [W only]'
                    },
                    {
                      value:
                        'Water withdrawn from water-stressed areas [W only]',
                      label:
                        'Water withdrawn from water-stressed areas [W only]'
                    },
                    {
                      value: 'Water discharge treatment data [W only]',
                      label: 'Water discharge treatment data [W only]'
                    },
                    {
                      value: 'Breaches to local water regulations [W only]',
                      label: 'Breaches to local water regulations [W only]'
                    },
                    {
                      value:
                        'Impingements on the human right to water in communities [W only]',
                      label:
                        'Impingements on the human right to water in communities [W only]'
                    },
                    {
                      value: 'Access to WASH in the workplace [W only]',
                      label: 'Access to WASH in the workplace [W only]'
                    },
                    {
                      value:
                        'Engagement with their value chain on environmental issues',
                      label:
                        'Engagement with their value chain on environmental issues'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_4_VALUE,
                  fieldLabel: 'Process through which information is obtained',
                  options: [
                    {
                      value: 'Directly from the client/investee',
                      label: 'Directly from the client/investee'
                    },
                    {
                      value: 'From an intermediary or business partner',
                      label: 'From an intermediary or business partner'
                    },
                    {value: 'Data provider', label: 'Data provider'},
                    {
                      value: 'Public data sources',
                      label: 'Public data sources'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_5_VALUE,
                  fieldLabel:
                    'Industry sectors covered by due diligence and/or risk assessment process',
                  options: INDUSTRY_SECTORS_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_6_VALUE,
                  fieldLabel:
                    '% of portfolio covered by the process in relation to total portfolio value',
                  colSpan: true
                },
                {
                  type: inputType.DISPLAY_CALCULATION_IN_TABLE,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_7_VALUE,
                  fieldLabel: 'Total portfolio value covered by the process',
                  colSpan: true,
                  relatedKey: [
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q11_6_VALUE,
                    CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q12_5_VALUE
                  ],
                  formula: ([percentageOfPortfolio, portfolioValue]: any[]):
                    | number
                    | string => {
                    const percentage = percentageOfPortfolio / 100;
                    const totaPortfolioValue = portfolioValue * percentage;

                    return totaPortfolioValue;
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.3 Have you identified priority locations across your value chain?',
            tooltip:
              'To effectively address nature-related issues and to ensure operational stability, organizations should prioritize their attention in or near areas with ecosystems whose current and future health and resilience are challenged. Identifying and prioritizing locations in or near areas with ecological sensitivity, as well as areas where the organization has substantive dependencies, impacts, risks, and opportunities related to nature allows the organization to focus on areas where action is needed most urgently, maximizing the potential for positive impacts.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_1_VALUE,
                  fieldLabel: 'Identification of priority locations',
                  options: [
                    {
                      value: 'Yes, we have identified priority locations',
                      label: 'Yes, we have identified priority locations'
                    },
                    {
                      value:
                        'Yes, we are currently in the process of identifying priority locations',
                      label:
                        'Yes, we are currently in the process of identifying priority locations'
                    },
                    {
                      value: 'No, but we plan to within the next two years',
                      label: 'No, but we plan to within the next two years'
                    },
                    {
                      value:
                        'No, and we do not plan to within the next two years',
                      label:
                        'No, and we do not plan to within the next two years'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_2_VALUE,
                  fieldLabel:
                    'Value chain stages where priority locations have been identified',
                  options: [
                    {value: 'Direct operations', label: 'Direct operations'},
                    {
                      value: 'Upstream value chain',
                      label: 'Upstream value chain'
                    },
                    {
                      value: 'Downstream value chain',
                      label: 'Downstream value chain'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_3_VALUE,
                  fieldLabel: 'Types of priority locations identified',
                  options: [
                    {
                      label: 'Sensitive locations',
                      options: [
                        {
                          value: 'Areas important for biodiversity',
                          label: 'Areas important for biodiversity'
                        },
                        {
                          value: 'Areas of high ecosystem integrity',
                          label: 'Areas of high ecosystem integrity'
                        },
                        {
                          value:
                            'Areas of rapid decline in ecosystem integrity',
                          label: 'Areas of rapid decline in ecosystem integrity'
                        },
                        {
                          value:
                            'Areas of limited water availability, flooding, and/or poor quality of water',
                          label:
                            'Areas of limited water availability, flooding, and/or poor quality of water'
                        },
                        {
                          value:
                            'Areas of importance for ecosystem service provision',
                          label:
                            'Areas of importance for ecosystem service provision'
                        },
                        {
                          value: 'Other sensitive location, please specify',
                          label: 'Other sensitive location, please specify'
                        }
                      ]
                    },
                    {
                      label:
                        'Locations with substantive dependencies, impacts, risks, and/or opportunities',
                      options: [
                        {
                          value:
                            'Locations with substantive dependencies, impacts, risks, and/or opportunities relating to forests',
                          label:
                            'Locations with substantive dependencies, impacts, risks, and/or opportunities relating to forests'
                        },
                        {
                          value:
                            'Locations with substantive dependencies, impacts, risks, and/or opportunities relating to water',
                          label:
                            'Locations with substantive dependencies, impacts, risks, and/or opportunities relating to water'
                        },
                        {
                          value:
                            'Locations with substantive dependencies, impacts, risks, and/or opportunities relating to biodiversity',
                          label:
                            'Locations with substantive dependencies, impacts, risks, and/or opportunities relating to biodiversity'
                        },
                        {
                          value:
                            'Other location with substantive nature-related dependencies, impacts, risks, and/or opportunities, please specify',
                          label:
                            'Other location with substantive nature-related dependencies, impacts, risks, and/or opportunities, please specify'
                        }
                      ]
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_4_VALUE,
                  fieldLabel:
                    'Description of process to identify priority locations',
                  colSpan: true,
                  textLimit: 3000
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_5_VALUE,
                  fieldLabel:
                    'Will you be disclosing a list/spatial map of priority locations?',
                  options: [
                    {
                      value:
                        'Yes, we will be disclosing the list/geospatial map of priority locations',
                      label:
                        'Yes, we will be disclosing the list/geospatial map of priority locations'
                    },
                    {
                      value:
                        'No, we have a list/geospatial map of priority locations, but we will not be disclosing it',
                      label:
                        'No, we have a list/geospatial map of priority locations, but we will not be disclosing it'
                    },
                    {
                      value:
                        'No, we do not have a list/geospatial map of priority locations',
                      label:
                        'No, we do not have a list/geospatial map of priority locations'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.FILEUPLOAD,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_6_VALUE,
                  fieldLabel:
                    'Provide a list and/or spatial map of priority locations',
                  colSpan: true,
                  fileUploadAction:
                    API_ENDPOINT_JSON.ESG.CDP_CORPORATE.documentUpload
                      .management,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_5_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, we will be disclosing the list/geospatial map of priority locations'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_5_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'Yes, we will be disclosing the list/geospatial map of priority locations'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_5_VALUE
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_7_VALUE,
                  fieldLabel:
                    'Primary reason for not identifying priority locations',
                  options:
                    PRIMARY_REASONS_FOR_NOT_ASSESSING_INTERCONNECTIONS_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_8_VALUE,
                  fieldLabel:
                    'Explain why you do not identify priority locations',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, but we plan to within the next two years',
                      'No, and we do not plan to within the next two years'
                    ],
                    required: true,
                    dependentValue:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q12_1_VALUE
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.4 How does your organization define substantive effects on your organization?',
            tooltip:
              'Indicators and thresholds employed to define which effects on the organization are considered substantive can differ among disclosers depending on their sector, value chain, or geography. Therefore, outlining clear definitions on how organizations determine potential effects as substantive provides critical context to data users. This question increases transparency and improves clarity as to what the discloser deems to be a substantive effect on their organization.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_1_VALUE,
                  fieldLabel: 'Effect type',
                  options: [
                    {value: 'Risks', label: 'Risks'},
                    {value: 'Opportunities', label: 'Opportunities'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_2_VALUE,
                  fieldLabel: 'Type of definition',
                  options: [
                    {value: 'Qualitative', label: 'Qualitative'},
                    {value: 'Quantitative', label: 'Quantitative'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_3_VALUE,
                  fieldLabel: 'Indicator used to define substantive effect',
                  options: [
                    {value: 'Asset value', label: 'Asset value'},
                    {value: 'Capital allocation', label: 'Capital allocation'},
                    {
                      value: 'Capital expenditures',
                      label: 'Capital expenditures'
                    },
                    {value: 'Credit risk', label: 'Credit risk'},
                    {
                      value: 'Customer complaints',
                      label: 'Customer complaints'
                    },
                    {
                      value: 'Direct operating costs',
                      label: 'Direct operating costs'
                    },
                    {value: 'EBITDA', label: 'EBITDA'},
                    {value: 'Employee turnover', label: 'Employee turnover'},
                    {
                      value: 'Indirect operating costs',
                      label: 'Indirect operating costs'
                    },
                    {value: 'Liabilities', label: 'Liabilities'},
                    {value: 'Market share', label: 'Market share'},
                    {
                      value: 'Production capacity',
                      label: 'Production capacity'
                    },
                    {value: 'Revenue', label: 'Revenue'},
                    {value: 'Share price', label: 'Share price'},
                    {value: 'Shareholder value', label: 'Shareholder value'},
                    {value: 'Stranded assets', label: 'Stranded assets'},
                    {
                      value: 'Strategic customers',
                      label: 'Strategic customers'
                    },
                    {
                      value: 'Other, please specify.',
                      label: 'Other, please specify.'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_4_VALUE,
                  fieldLabel: 'Change to indicator',
                  options: [
                    {value: 'Absolute decrease', label: 'Absolute decrease'},
                    {value: 'Absolute increase', label: 'Absolute increase'},
                    {value: '% decrease', label: '% decrease'},
                    {value: '% increase', label: '% increase'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_5_VALUE,
                  fieldLabel: '% change to indicator',
                  options: [
                    {value: 'Less than 1%', label: 'Less than 1%'},
                    {value: '1-10', label: '1-10'},
                    {value: '11-20', label: '11-20'},
                    {value: '21-30', label: '21-30'},
                    {value: '31-40', label: '31-40'},
                    {value: '41-50', label: '41-50'},
                    {value: '51-60', label: '51-60'},
                    {value: '61-70', label: '61-70'},
                    {value: '71-80', label: '71-80'},
                    {value: '81-90', label: '81-90'},
                    {value: '91-99', label: '91-99'},
                    {value: '100%', label: '100%'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_2_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: ['Quantitative']
                  }
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_6_VALUE,
                  fieldLabel: 'Absolute increase/ decrease figure',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_7_VALUE,
                  fieldLabel: 'Metrics considered in definition',
                  options: [
                    {
                      value: 'Frequency of effect occurring',
                      label: 'Frequency of effect occurring'
                    },
                    {
                      value: 'Time horizon over which the effect occurs',
                      label: 'Time horizon over which the effect occurs'
                    },
                    {
                      value: 'Likelihood of effect occurring',
                      label: 'Likelihood of effect occurring'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_8_VALUE,
                  fieldLabel: 'Application of definition',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q13_7_VALUE,
                    condition: conditionType.NOT_INCLUDES,
                    values: [
                      'Frequency of effect occurring',
                      'Time horizon over which the effect occurs',
                      'Likelihood of effect occurring',
                      'Other, please specify'
                    ]
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.5 Does your organization identify and classify potential water pollutants associated with its activities that could have a detrimental impact on water ecosystems or human health?',
            tooltip:
              'Water pollutants pose a threat to the quality of surface and groundwater bodies and their dependent ecosystems. This question allows organizations to indicate that they identify and classify the potential water pollutants associated with the substances they handle and the properties of their discharges. It is important that companies identify and classify potential water pollutants linked to their business operations and products, and are able to effectively manage these'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE,
                  fieldLabel:
                    'Identification and classification of potential water pollutants',
                  options: [
                    {
                      value:
                        'Yes, we identify and classify our potential water pollutants',
                      label:
                        'Yes, we identify and classify our potential water pollutants'
                    },
                    {
                      value:
                        'No, we do not identify and classify our potential water pollutants',
                      label:
                        'No, we do not identify and classify our potential water pollutants'
                    },
                    {value: 'Unknown', label: 'Unknown'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_2_VALUE,
                  fieldLabel:
                    'How potential water pollutants are identified and classified',
                  colSpan: true,
                  textLimit: 2500,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, we identify and classify our potential water pollutants'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'Yes, we identify and classify our potential water pollutants'
                    ],
                    required: true
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_3_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 1000,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'No, we do not identify and classify our potential water pollutants',
                      'Unknown'
                    ]
                  },
                  requiredOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE,
                    condition: conditionType.EQUALS,
                    values: [
                      'No, we do not identify and classify our potential water pollutants',
                      'Unknown'
                    ],
                    required: true
                  }
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.5.1 Describe how your organization minimizes the adverse impacts of potential water pollutants on water ecosystems or human health associated with your activities.',
            tooltip:
              'Organizations are able to describe the company-specific actions and procedures they have in place to minimize the adverse impacts associated with the water pollutants identified. This will demonstrate to data users the organizations commitment to address these water-related risks.',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_VALUE,
              showWhen: [
                'Yes, we identify and classify our potential water pollutants'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q15_VALUE,
            dependsOn:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_1_VALUE,
            showWhen: [
              'Yes, we identify and classify our potential water pollutants'
            ],
            relatedTableKey:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q14_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q15_1_VALUE,
                  fieldLabel: 'Water pollutant category',
                  options: [
                    {
                      value: 'Inorganic pollutants',
                      label: 'Inorganic pollutants'
                    },
                    {value: 'Oil', label: 'Oil'},
                    {value: 'Nitrates', label: 'Nitrates'},
                    {value: 'Phosphates', label: 'Phosphates'},
                    {
                      value: 'Other nutrients and oxygen demanding pollutants',
                      label: 'Other nutrients and oxygen demanding pollutants'
                    },
                    {value: 'Pesticides', label: 'Pesticides'},
                    {
                      value: 'Other synthetic organic compounds',
                      label: 'Other synthetic organic compounds'
                    },
                    {value: 'Pathogens', label: 'Pathogens'},
                    {
                      value: 'Microplastics and plastic particles',
                      label: 'Microplastics and plastic particles'
                    },
                    {
                      value: 'Other physical pollutants',
                      label: 'Other physical pollutants'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q15_2_VALUE,
                  fieldLabel:
                    'Description of water pollutant and potential impacts',
                  colSpan: true,
                  textLimit: 1500
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q15_3_VALUE,
                  fieldLabel: 'Value chain stage',
                  options: [
                    {value: 'Direct operation', label: 'Direct operation'},
                    {
                      value: 'Upstream value chain',
                      label: 'Upstream value chain'
                    },
                    {
                      value: 'Downstream value chain',
                      label: 'Downstream value chain'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q15_4_VALUE,
                  fieldLabel:
                    'Actions and procedures to minimize adverse impacts',
                  options: [
                    {
                      value:
                        'Assessment of critical infrastructure and storage condition (leakages, spillages, pipe erosion etc.) and their resilience',
                      label:
                        'Assessment of critical infrastructure and storage condition (leakages, spillages, pipe erosion etc.) and their resilience'
                    },
                    {value: 'Resource recovery', label: 'Resource recovery'},
                    {
                      value: 'Beyond compliance with regulatory requirements',
                      label: 'Beyond compliance with regulatory requirements'
                    },
                    {
                      value:
                        'Implementation of integrated solid waste management systems',
                      label:
                        'Implementation of integrated solid waste management systems'
                    },
                    {
                      value:
                        'Industrial and chemical accidents prevention, preparedness, and response',
                      label:
                        'Industrial and chemical accidents prevention, preparedness, and response'
                    },
                    {
                      value:
                        'Provision of best practice instructions on product use',
                      label:
                        'Provision of best practice instructions on product use'
                    },
                    {value: 'Water recycling', label: 'Water recycling'},
                    {
                      value: 'Reduction or phase out of hazardous substances',
                      label: 'Reduction or phase out of hazardous substances'
                    },
                    {
                      value:
                        'Requirement for suppliers to comply with regulatory requirements',
                      label:
                        'Requirement for suppliers to comply with regulatory requirements'
                    },
                    {
                      value:
                        'Discharge treatment using sector-specific processes to ensure compliance with regulatory requirements',
                      label:
                        'Discharge treatment using sector-specific processes to ensure compliance with regulatory requirements'
                    },
                    {
                      value: 'Upgrading of process equipment/methods',
                      label: 'Upgrading of process equipment/methods'
                    },
                    {
                      value: 'Procedure(s) under development/ R&D',
                      label: 'Procedure(s) under development/ R&D'
                    },
                    {
                      value: 'No formal procedure(s) in place',
                      label: 'No formal procedure(s) in place'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q15_5_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 1500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.6 By river basin, what number of active and inactive tailings dams are within your control?',
            tooltip:
              'Ensuring the best management of tailings dams is a critical water-related issue for organizations in the Metals & Mining and Coal sectors. Operational requirements and local factors such as climate or landscape determine the dams’ type and design and subsequently the risk level for this task. Active and inactive tailings dams pose different risks, therefore requiring different management procedures. These questions will demonstrate to data users that organizations understand these issues, are assessing the risks associated, and are taking action to prevent them. Disclosure on the locations and state of the tailings dams will allow data users to relate this information to other responses in the CDP questionnaire that include country/area/region and basin specific information in Module 9.'
            // conditionalVisibility: {
            //   dependsOn: CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q15_VALUE,
            //   showWhen: ['Surface coal mining', 'Underground coal mining','Metal Ore mining','Other minerals mining'],
            //   condition: conditionType.INCLUDES,
            //   fieldName: CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q15_VALUE
            // }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q16_VALUE,
            // dependsOn: CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q15_VALUE,
            // showWhen: ['Surface coal mining', 'Underground coal mining','Metal Ore mining','Other minerals mining'],
            // relatedTableKey: CDP_CORPORATE_INTRO_FIELD_NAMES.CORPORATE_INTRO_Q15_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q16_1_VALUE,
                  fieldLabel: 'Country/area & River basin',
                  options: RIVER_BASIN_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q16_2_VALUE,
                  fieldLabel: 'Number of tailings dams in operation',
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q16_3_VALUE,
                  fieldLabel: 'Number of inactive tailings dams',
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q16_4_VALUE,
                  fieldLabel: 'Comment',
                  colSpan: true,
                  textLimit: 1500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.6.1 Do you evaluate and classify the tailings dams under your control according to the consequences of their failure to human health and ecosystems?',
            tooltip:
              'Evaluating potential losses or damages to human health and ecosystems, irrespective of the perceived probability of failure, provides evidence to investors and other data users that an organization has an awareness of the severe risks as well as potential liabilities associated with poor management of tailings dams. It also helps data users interpret the disclosed information on management procedures for these tasks (dams). If your organization responded to the Investor Mining and Tailings Safety Initiative 2019 disclosure request, you may find it useful to draw on that submission when completing these questions.'
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_VALUE,
            allowMultipleRows: false,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_1_VALUE,
                  fieldLabel:
                    'Evaluation of the consequences of tailings dam failure',
                  options: [
                    {
                      value:
                        'Yes, we evaluate the consequences of tailings dam failure',
                      label:
                        'Yes, we evaluate the consequences of tailings dam failure'
                    },
                    {
                      value:
                        'No, we do not currently evaluate the consequences of tailings dam failure but we plan to do so within the next two years',
                      label:
                        'No, we do not currently evaluate the consequences of tailings dam failure but we plan to do so within the next two years'
                    },
                    {
                      value:
                        'No, we do not evaluate the consequences of tailings dam failure and have no plans to do so within the next two years',
                      label:
                        'No, we do not evaluate the consequences of tailings dam failure and have no plans to do so within the next two years'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.MULTISELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_2_VALUE,
                  fieldLabel: 'Evaluation/Classification guideline(s)',
                  options: [
                    {
                      value:
                        'Australian National Committee on Large Dams (ANCOLD)',
                      label:
                        'Australian National Committee on Large Dams (ANCOLD)'
                    },
                    {
                      value: 'Canadian Dam Association (CDA)',
                      label: 'Canadian Dam Association (CDA)'
                    },
                    {
                      value:
                        'Ordinance 70.389/17 – Mining National Agency, Brazil',
                      label:
                        'Ordinance 70.389/17 – Mining National Agency, Brazil'
                    },
                    {
                      value: 'Ghana Minerals Commission (LI 2182)',
                      label: 'Ghana Minerals Commission (LI 2182)'
                    },
                    {
                      value: 'South Africa SANS 10286',
                      label: 'South Africa SANS 10286'
                    },
                    {
                      value: 'International Commission on Large Dams (ICOLD)',
                      label: 'International Commission on Large Dams (ICOLD)'
                    },
                    {
                      value:
                        'Global Industry Standard on Tailings Management (ICMM)',
                      label:
                        'Global Industry Standard on Tailings Management (ICMM)'
                    },
                    {
                      value: 'Company-specific guidelines',
                      label: 'Company-specific guidelines'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, we evaluate the consequences of tailings dam failure'
                    ]
                  }
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_3_VALUE,
                  fieldLabel:
                    'Tailings dams have been classified as ‘hazardous’ or ‘highly hazardous’',
                  options: [
                    {
                      value:
                        'Yes, tailings dams have been classified as hazardous or highly hazardous (or equivalent)',
                      label:
                        'Yes, tailings dams have been classified as hazardous or highly hazardous (or equivalent)'
                    },
                    {
                      value:
                        'None of our tailings dams have been classified as ‘hazardous’ or ‘highly hazardous’ (or equivalent)',
                      label:
                        'None of our tailings dams have been classified as ‘hazardous’ or ‘highly hazardous’ (or equivalent)'
                    },
                    {value: 'Don’t know', label: 'Don’t know'}
                  ],
                  colSpan: true,
                  disabledOn: {
                    fieldName:
                      CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_1_VALUE,
                    condition: conditionType.NOT,
                    values: [
                      'Yes, we evaluate the consequences of tailings dam failure'
                    ]
                  }
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_4_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 1500
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.6.2 Provide details for all dams classified as ‘hazardous’ or ‘highly hazardous’.',
            tooltip:
              'Providing details for ‘hazardous’ and ‘highly hazardous’ tailings dams demonstrates a commitment to transparency around the potentially devastating consequences of tailings dam failures. Despite the existence of a variety of categorization systems and guidelines for evaluating the consequences of dam failure to human health and ecosystems, there is no standardized definition of what constitutes ‘Hazardous’ or ‘Highly hazardous’ tailings dams. Disclosing the guidelines used together with the current and planned impoundment volumes will also demonstrate data users that a company is aware of the potential risks and how this exposure may change in the near future. Requesting information about the location of the dam will allow data users to relate this information to other responses in the CDP questionnaire that include country/area and basin specific information. The information requested in this question aligns with the request by the Investor Mining and Tailings Safety Initiative, Sustainability Accounting Standards Board (SASB) and Global Reporting Initiative (GRI).',
            conditionalVisibility: {
              dependsOn:
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_VALUE,
              showWhen: [
                'Yes, tailings dams have been classified as hazardous or highly hazardous (or equivalent)'
              ],
              condition: conditionType.EQUALS,
              fieldName:
                CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_3_VALUE
            }
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_VALUE,
            dependsOn:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_3_VALUE,
            showWhen: [
              'Yes, tailings dams have been classified as hazardous or highly hazardous (or equivalent)'
            ],
            relatedTableKey:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q17_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_1_VALUE,
                  fieldLabel: 'Tailings dam name / identifier',
                  colSpan: true,
                  textLimit: 100
                },
                {
                  type: inputType.SELECT_WITH_OTHER_TEXTBOX,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_2_VALUE,
                  fieldLabel: 'Country / Area & River basin',
                  options: RIVER_BASIN_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_3_VALUE,
                  fieldLabel: 'Latitude',
                  colSpan: true,
                  roundTo: 6
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_4_VALUE,
                  fieldLabel: 'Longitude',
                  colSpan: true,
                  roundTo: 6
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_5_VALUE,
                  fieldLabel: 'Hazard classification',
                  colSpan: true,
                  textLimit: 100
                },
                {
                  type: inputType.MULTISELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_6_VALUE,
                  fieldLabel: 'Guidelines used',
                  options: [
                    {
                      value:
                        'Australian National Committee on Large Dams (ANCOLD)',
                      label:
                        'Australian National Committee on Large Dams (ANCOLD)'
                    },
                    {
                      value: 'Canadian Dam Association (CDA)',
                      label: 'Canadian Dam Association (CDA)'
                    },
                    {
                      value:
                        'Ordinance 70.389/17 – Mining National Agency, Brazil',
                      label:
                        'Ordinance 70.389/17 – Mining National Agency, Brazil'
                    },
                    {
                      value: 'Ghana Minerals Commission (LI 2182)',
                      label: 'Ghana Minerals Commission (LI 2182)'
                    },
                    {
                      value: 'South Africa SANS 10286',
                      label: 'South Africa SANS 10286'
                    },
                    {
                      value: 'International Commission on Large Dams (ICOLD)',
                      label: 'International Commission on Large Dams (ICOLD)'
                    },
                    {
                      value:
                        'Global Industry Standard on Tailings Management (ICMM)',
                      label:
                        'Global Industry Standard on Tailings Management (ICMM)'
                    },
                    {
                      value: 'Company-specific guidelines',
                      label: 'Company-specific guidelines'
                    },
                    {
                      value: 'Other, please specify',
                      label: 'Other, please specify'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_7_VALUE,
                  fieldLabel: 'Tailings dam’s activity',
                  options: [
                    {value: 'Active', label: 'Active'},
                    {value: 'Inactive', label: 'Inactive'}
                  ],
                  colSpan: true
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_8_VALUE,
                  fieldLabel:
                    'Current tailings storage impoundment volume (Mm3)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.NUMBER,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_9_VALUE,
                  fieldLabel:
                    'Planned tailings storage impoundment volume in 5 years(Mm3)',
                  colSpan: true,
                  roundTo: 2
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q18_10_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 1000
                }
              ]
            }
          }
        ]
      },
      {
        field: [
          {
            fieldType: inputType.HEADING,
            heading:
              '2.6.3 To manage the potential impacts to human health or water ecosystems associated with the tailings dams in your control, what procedures are in place for all of your dams?',
            tooltip:
              'Sound governance and precautionary procedures are needed to address the potential risks linked to these tasks. Providing information on existing procedures will demonstrate to investors and data users that organizations are dealing with the potential risks associated with the tailings dams under their control.The question provides organizations with the chance to indicate and explain their company-specific management procedures, how these procedures are set, their scope, and their efficacy. '
          },
          {
            fieldType: inputType.ADD_NEW_TO_TABLE,
            fieldName:
              CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q19_VALUE,
            formList: {
              formFields: [
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q19_1_VALUE,
                  fieldLabel: 'Procedure',
                  options: [
                    {
                      value: 'Acceptable risk levels',
                      label: 'Acceptable risk levels'
                    },
                    {value: 'Operating plan', label: 'Operating plan'},
                    {
                      value: 'Life of facility plan',
                      label: 'Life of facility plan'
                    },
                    {value: 'Assurance program', label: 'Assurance program'},
                    {
                      value: 'Change management process',
                      label: 'Change management process'
                    },
                    {value: 'Approval', label: 'Approval'},
                    {
                      value: 'Other management procedure',
                      label: 'Other management procedure'
                    },
                    {
                      value:
                        'No management procedure applies to all tailings dams',
                      label:
                        'No management procedure applies to all tailings dams'
                    }
                  ],
                  colSpan: true
                },
                {
                  type: inputType.SELECT,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q19_2_VALUE,
                  fieldLabel: 'Detail of the procedure',
                  options: DETAILS_OF_PROCEDURE_OPTIONS,
                  colSpan: true
                },
                {
                  type: inputType.WYSIWYG,
                  fieldName:
                    CDP_CORPORATE_MANAGEMENT_FIELD_NAMES.CORPORATE_MANAGEMENT_Q19_3_VALUE,
                  fieldLabel: 'Please explain',
                  colSpan: true,
                  textLimit: 2500
                }
              ]
            }
          }
        ]
      }
    ]
  }
];
